import { HeadingNode, HeadingTagType, SerializedHeadingNode } from '@lexical/rich-text';
import {
  LexicalNode,
  EditorConfig,
  NodeKey,
  $applyNodeReplacement,
  LexicalUpdateJSON,
} from 'lexical';

import { setNodeLineHeightToDOM } from '../Plugins/TextBlockPlugin/utils';
import { BlockNode, calcFontSize } from './BlockNode';

const NODE_TYPE = 'custom-heading';

export type SerializedCustomHeadingNode = SerializedHeadingNode & {
  type: typeof NODE_TYPE;
  lineHeight?: number;
  fontSize?: number;
};

/**
* Custom Heading Node with dynamic line-height functionality
*/
export class CustomHeadingNode extends HeadingNode implements BlockNode {
  __lineHeight: number | undefined;

  __fontSize: number | undefined;

  constructor(tag: HeadingTagType, lineHeight?: number, fontSize?: number, key?: NodeKey) {
    super(tag, key);
    this.__lineHeight = lineHeight;
    this.__fontSize = fontSize;
  }

  static getType(): typeof NODE_TYPE {
    return NODE_TYPE;
  }

  static clone(node: CustomHeadingNode): CustomHeadingNode {
    return new CustomHeadingNode(node.__tag, node.__lineHeight, node.__fontSize, node.__key);
  }

  createDOM(config: EditorConfig): HTMLElement {
    const element = super.createDOM(config);
    element.style.fontSize = calcFontSize(this.__fontSize);
    setNodeLineHeightToDOM(element, this.__lineHeight, this.__fontSize);

    return element;
  }

  updateDOM(prevNode: CustomHeadingNode, element: HTMLElement, config: EditorConfig): boolean {
    const isUpdated = super.updateDOM(prevNode as this, element, config);

    const prevLineHeight = prevNode.__lineHeight;
    const nextLineHeight = this.__lineHeight;
    const prevSize = prevNode.__fontSize;
    const nextSize = this.__fontSize;

    if ((prevLineHeight !== nextLineHeight) || (prevSize !== nextSize)) {
      element.style.fontSize = calcFontSize(nextSize);
      setNodeLineHeightToDOM(element, this.__lineHeight, this.__fontSize);
    }

    return isUpdated;
  }

  updateFromJSON(
    serializedNode: LexicalUpdateJSON<SerializedCustomHeadingNode>,
  ): this {
    super.updateFromJSON(serializedNode);

    return this
      .setLineHeight(serializedNode.lineHeight)
      .updateFontSize(serializedNode.fontSize);
  }

  static importJSON(
    serializedNode: SerializedCustomHeadingNode,
  ): CustomHeadingNode {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define, no-use-before-define
    return $createCustomHeadingNode(serializedNode.tag).updateFromJSON(serializedNode);
  }

  exportJSON(): SerializedCustomHeadingNode {
    const json = super.exportJSON();

    return {
      ...json,
      type: CustomHeadingNode.getType(),
      lineHeight: this.getLineHeight(),
      fontSize: this.getFontSize(),
    };
  }

  setLineHeight(lineHeight: number | undefined): this {
    this.getWritable().__lineHeight = lineHeight;

    return this;
  }

  getLineHeight(): number | undefined {
    return this.getLatest().__lineHeight;
  }

  setFontSize(fontSize: number | undefined): this {
    this.getWritable().__fontSize = fontSize;

    return this;
  }

  getFontSize(): number | undefined {
    return this.getLatest().__fontSize;
  }

  updateFontSize(fontSize: number | undefined): this {
    if (this.getFontSize() === fontSize) {
      return this;
    }

    return this.setFontSize(fontSize);
  }

}

export function $createCustomHeadingNode(tag: HeadingTagType, lineHeight?: number, fontSize?: number): CustomHeadingNode {
  return $applyNodeReplacement(new CustomHeadingNode(tag, lineHeight, fontSize));
}

export function $isCustomHeadingNode(
  node: LexicalNode | null | undefined,
): node is CustomHeadingNode {
  return node instanceof CustomHeadingNode;
}
